<div [formGroup]="formGroup" class="form-group gw-relative {{ class }}">
  <ng-container *ngIf="!isLoading; else skeleton" class="gw-p-small">
    <div class="gw-flex gw-flex-col gw-h-[72px]">
      <label *ngIf="label" [attr.for]="id" class="gw-form-label gw-h-[16px]">
        {{ label | translate }}

        <span
          *ngIf="info"
          class="gw-inline-flex gw-items-center"
          [tuiTooltip]="info | translate"
        >
          <gatewee-icon
            icon="@phi.info"
            iconStyle="primary"
            size="14px"
            class="gw-text-text-action"
          ></gatewee-icon>
        </span>
        <span *ngIf="required" class="gw-text-status-negative">*</span>
      </label>

      <div class="gw-input-group">
        <ng-container [ngSwitch]="type">
          <!-- Currency (legacy: ngx-mask, migrate screens to type=currencyTaiga when ready) -->
          <tui-textfield
            *ngSwitchCase="'currency'"
            tuiTextfieldSize="m"
            class="gw-w-full"
            [class.switch-input]="switch"
            [class.has-icon-start]="iconName"
            [iconStart]="iconName || null"
            [tuiTextfieldCleaner]="!readonly && !control.disabled"
          >
            <input
              tuiTextfield
              type="text"
              [id]="id"
              [attr.name]="id"
              [formControl]="control"
              [placeholder]="placeholder"
              mask="separator.2"
              [autocomplete]="autocomplete ? 'on' : 'off'"
              thousandSeparator="."
              decimalMarker=","
              [ngClass]="inputClasses"
              [readonly]="readonly"
              (blur)="onBlur($event)"
              class="input-v2"
            />
          </tui-textfield>

          <!-- Currency (digit-cent entry; lead slot = R$/symbol or iconStart) -->
          <tui-textfield
            *ngSwitchCase="'currencyTaiga'"
            tuiTextfieldSize="m"
            class="gw-w-full"
            [class.gw-currency-taiga-with-prefix]="!iconName"
            [class.switch-input]="switch"
            [class.has-icon-start]="true"
            [iconStart]="iconName || null"
            [tuiTextfieldCleaner]="!readonly && !control.disabled"
          >
            <span
              *ngIf="!iconName"
              class="gw-currency-taiga-lead gw-pointer-events-none gw-select-none gw-self-center gw-pl-[10px] gw-pr-1 gw-text-[15px] gw-font-semibold gw-leading-none gw-text-text-secondary"
              aria-hidden="true"
            >
              {{ currencyTaigaLeadSymbol }}
            </span>
            <input
              tuiTextfield
              gateweeCurrencyDigits
              type="text"
              inputmode="numeric"
              [id]="id"
              [attr.name]="id"
              [formControl]="control"
              [placeholder]="placeholder"
              [autocomplete]="autocomplete ? 'on' : 'off'"
              [ngClass]="inputClasses"
              [readonly]="readonly"
              (blur)="onBlur($event)"
              class="input-v2"
            />
          </tui-textfield>

          <!-- Number Input -->
          <tui-textfield
            *ngSwitchCase="'number'"
            tuiTextfieldSize="m"
            class="gw-w-full"
            [class.less-icon]="!iconName"
            [class.has-icon-start]="iconName"
            [iconStart]="iconName || null"
            [tuiTextfieldCleaner]="!readonly && !control.disabled"
          >
            <input
              tuiTextfield
              type="number"
              [id]="id"
              [attr.name]="id"
              [formControl]="control"
              [placeholder]="placeholder"
              [max]="max"
              [min]="min"
              [autocomplete]="autocomplete ? 'on' : 'off'"
              [ngClass]="inputClasses"
              [readonly]="readonly"
              (blur)="onBlur($event)"
              class="input-v2"
            />
          </tui-textfield>

          <!-- Rate Input -->
          <tui-textfield
            *ngSwitchCase="'rate'"
            tuiTextfieldSize="m"
            class="gw-w-full"
            [class.has-icon-start]="iconName"
            [iconStart]="iconName || null"
            [tuiTextfieldCleaner]="!readonly && !control.disabled"
          >
            <input
              tuiTextfield
              type="text"
              [id]="id"
              [attr.name]="id"
              [formControl]="control"
              [placeholder]="placeholder"
              mask="separator.2"
              thousandSeparator=""
              [decimalMarker]="['.', ',']"
              [autocomplete]="autocomplete ? 'on' : 'off'"
              inputmode="decimal"
              maxlength="5"
              [ngClass]="inputClasses"
              [readonly]="readonly"
              (blur)="onBlur($event)"
            />
          </tui-textfield>

          <!-- Decimal Input -->
          <tui-textfield
            *ngSwitchCase="'decimal'"
            tuiTextfieldSize="m"
            class="gw-w-full"
            [class.has-icon-start]="iconName"
            [iconStart]="iconName || null"
            [tuiTextfieldCleaner]="!readonly && !control.disabled"
          >
            <input
              tuiTextfield
              type="text"
              [id]="id"
              [attr.name]="id"
              [formControl]="control"
              [placeholder]="placeholder"
              mask="separator.2"
              thousandSeparator=""
              [decimalMarker]="['.', ',']"
              [autocomplete]="autocomplete ? 'on' : 'off'"
              inputmode="decimal"
              [ngClass]="inputClasses"
              [readonly]="readonly"
              (blur)="onBlur($event)"
              class="input-v2"
            />
          </tui-textfield>

          <!-- Password Input -->
          <tui-textfield
            *ngSwitchCase="'password'"
            tuiTextfieldSize="m"
            class="gw-w-full gw-relative"
            [class.less-icon]="!iconName"
            [class.has-icon-start]="iconName"
            [iconStart]="iconName || null"
            [tuiTextfieldCleaner]="false"
          >
            <input
              tuiTextfield
              [type]="'password'"
              [id]="id"
              [attr.name]="id"
              [formControl]="control"
              [placeholder]="placeholder"
              [mask]="mask"
              [attr.maxlength]="max"
              [autocomplete]="autocomplete ? 'on' : 'off'"
              [ngClass]="inputClasses"
              [readonly]="readonly"
              (blur)="onBlur($event)"
              class="input-v2"
            />
            <tui-icon tuiPassword />
          </tui-textfield>

          <!-- Default Input (text, email, etc.) -->
          <tui-textfield
            *ngSwitchDefault
            tuiTextfieldSize="m"
            class="gw-w-full gw-relative"
            [class.less-icon]="!iconName"
            [class.has-icon-start]="iconName"
            [iconStart]="iconName || null"
            [tuiTextfieldCleaner]="!readonly && !control.disabled"
          >
            <input
              tuiTextfield
              [type]="type || 'text'"
              [id]="id"
              [attr.name]="id"
              [formControl]="control"
              [placeholder]="placeholder"
              [mask]="mask"
              [attr.maxlength]="max"
              [autocomplete]="autocomplete ? 'on' : 'off'"
              [ngClass]="inputClasses"
              [readonly]="readonly"
              (focus)="onFocus($event)"
              (blur)="onBlur($event)"
              class="input-v2"
            />
          </tui-textfield>
        </ng-container>

        <div
          *ngIf="(type === 'currency' || type === 'currencyTaiga') && switch"
          class="currency-toggle gw-h-[42px] gw-flex gw-items-center gw-justify-center gw-px-[12px] gw-border gw-border-solid gw-border-border-normal gw-border-l-0 gw-rounded-br-[12px] gw-rounded-tr-[12px]"
        >
          <div
            (click)="onSwitchToggle()"
            [attr.aria-label]="
              isWithdrawalAmountSwitch ? (switchAriaLabelKey | translate) : null
            "
            [tuiHint]="
              isWithdrawalAmountSwitch ? (switchHintKey | translate) : null
            "
            tuiHintDirection="top"
            class="gw-bg-background-accent-1 gw-relative gw-w-[48px] gw-h-[24px] gw-rounded-full gw-flex gw-items-center gw-px-[3px] gw-cursor-pointer gw-transition-all gw-duration-300 gw-overflow-hidden"
          >
            <div
              class="currency-toggle-knob gw-w-[18px] gw-h-[18px] gw-shrink-0 gw-rounded-full gw-bg-white gw-shadow gw-flex gw-items-center gw-justify-center"
              [class.currency-toggle-knob--currency]="isSwitchKnobRight"
            >
              <ng-container
                *ngIf="isWithdrawalAmountSwitch; else currencySwitchIcons"
              >
                <gatewee-icon
                  *ngIf="isAddFeesMode"
                  class="gw-text-[12px]"
                  iconStyle="primary"
                  icon="@phi.user"
                  size="16px"
                ></gatewee-icon>
                <gatewee-icon
                  *ngIf="!isAddFeesMode"
                  class="gw-text-[12px]"
                  iconStyle="primary"
                  icon="@phi.wallet"
                  size="16px"
                ></gatewee-icon>
              </ng-container>
              <ng-template #currencySwitchIcons>
                <ng-container [ngSwitch]="isCurrency">
                  <gatewee-icon
                    *ngSwitchCase="true"
                    class="gw-text-[12px]"
                    iconStyle="primary"
                    icon="@phi.currency-dollar"
                    size="16px"
                  ></gatewee-icon>
                  <gatewee-icon
                    *ngSwitchDefault
                    icon="@phi.percent"
                    iconStyle="primary"
                    size="12px"
                  ></gatewee-icon>
                </ng-container>
              </ng-template>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div
      *ngIf="hint || showFeedbackError"
      class="input-feedback gw-flex gw-flex-col gw-gap-1 gw-mt-1"
    >
      <small *ngIf="hint" class="input-hint gw-text-text-secondary gw-text-xs">
        <i [class]="hint.icon"></i>
        {{ hint.message | translate }}
      </small>
      <error-message
        *ngIf="control"
        [control]="control"
        [customMessages]="customMessages"
        [submitted]="submitted"
        [stacked]="true"
      ></error-message>
    </div>
  </ng-container>

  <ng-template #skeleton>
    <gatewee-skeleton></gatewee-skeleton>
  </ng-template>
</div>
